import { ApolloClient, HttpLink, InMemoryCache, from, gql } from '@apollo/client'
import { Observable, offsetLimitPagination } from '@apollo/client/utilities'
import { onError } from '@apollo/client/link/error'
import { setContext } from '@apollo/client/link/context'

const OPERATION_REFRESH_ACCESS_TOKEN = gql`
  mutation RefreshAccessToken($refresh_token: String!) {
    refreshAccessToken(refresh_token: $refresh_token) {
      access_token
    }
  }
`
const refreshToken = async () => {
  try {
    const { data } = await client.mutate({
      mutation: OPERATION_REFRESH_ACCESS_TOKEN,
      variables: {
        refresh_token: localStorage.getItem('REFRESH_TOKEN')
      }
    })
    return {
      data: data.refreshAccessToken.access_token
    }
  } catch (err) {
    localStorage.clear()
    return {
      error: err
    }
  }
}

const httpLink = new HttpLink({
  uri: process.env.REACT_APP_GRAPH_QL,
  credentials: 'include'
})

const authLink = setContext(({ operationName }, { headers }) => {
  const token = localStorage.getItem('ACCESS_TOKEN')
  if (operationName && ['Login', 'RefreshAccessToken'].includes(operationName)) {
    return {}
  }
  return {
    headers: token ? { ...headers, authorization: token } : headers
  }
})

const errorLink = onError(({ graphQLErrors, operation, networkError, forward }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (
        err.extensions?.code === 'UNAUTHENTICATED' &&
        operation.operationName.toLowerCase() !== 'refreshaccesstoken'
      ) {
        return new Observable(observer => {
          ;(async () => {
            try {
              const { data, error } = await refreshToken()
              if (!data || error) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                window.location = '/auth/login'
                return
              }
              localStorage.setItem('ACCESS_TOKEN', data)
              const subscriber = {
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer)
              }

              forward(operation).subscribe(subscriber)
            } catch (err) {
              observer.error(err)
            }
          })()
        })
      }
    }
  } else if (networkError) {
    console.log(`[Network error]: ${networkError}`)
  }
})

const additiveLink = from([errorLink, authLink, httpLink])

const pageLimitPagination = (keyArgs = []) => ({
  keyArgs,
  merge(existing, incoming, { args }) {
    if (!existing || !args?.page || args.page === 1) {
      return incoming
    }

    const page = args.page
    const limit = args.limit || incoming.data.length
    const offset = (page - 1) * limit
    const mergedData = existing.data ? existing.data.slice(0) : []

    incoming.data.forEach((item, index) => {
      mergedData[offset + index] = item
    })

    return {
      ...incoming,
      data: mergedData
    }
  }
})

const client = new ApolloClient({
  link: additiveLink,
  cache: new InMemoryCache({
    typePolicies: {
      Account: {
        keyFields: ['sf_id']
      },
      ProductAnalytics: {
        keyFields: ['period']
      },
      Query: {
        fields: {
          monitoringSitesByGeoloc: {
            keyArgs: false,
            merge(existing = [], incoming, { readField }) {
              const existingIds = new Set(
                existing.map(account => {
                  const site = readField('site', account)
                  return readField('id', site)
                })
              )
              const newAccounts = incoming.filter(account => {
                const site = readField('site', account)
                const siteId = readField('id', site)
                return !existingIds.has(siteId)
              })
              return [...existing, ...newAccounts]
            }
          },
          portalAdvertisements: offsetLimitPagination([
            'id',
            'statuses',
            'verbatim',
            'site_sf_ids',
            'publish_start_at',
            'publish_end_at',
            'sort_by',
            'sort_direction'
          ]),
          findRanRooms: offsetLimitPagination([
            'sf_id',
            'verbatim',
            'created_start_at',
            'created_end_at',
            'sort_by',
            'sort_direction'
          ]),
          searchCoupons: offsetLimitPagination(['type', 'verbatim']),
          searchAudinDocuments: offsetLimitPagination(),
          searchContact: offsetLimitPagination(['sf_id', 'internal']),
          findContactSites: offsetLimitPagination(['sf_id']),
          statisticsPortalUsers: offsetLimitPagination([
            'id',
            'site_sf_ids',
            'verbatim',
            'countries',
            'created_at_start',
            'created_at_end',
            'sort_by',
            'sort_direction'
          ]),
          projects: offsetLimitPagination(['site_sf_ids', 'state', 'services', 'sort_by', 'sort_direction']),
          mailingCampaigns: pageLimitPagination(),
          mailingTriggers: pageLimitPagination(),
          mailingEmailLogs: pageLimitPagination(['verbatim', 'delivery_type', 'source_id']),
          bypasses: {
            keyArgs: ['site_sf_id', 'verbatim', 'active', 'inactive', 'sort_by', 'sort_direction'],
            merge: false // Pas de fusion automatique, on gère tout manuellement avec updateQuery
          }
        }
      }
    }
  })
})

export const deserializeErrors = ({ graphQLErrors = [], networkError, ...error }) => {
  if (graphQLErrors.length) {
    const messages = graphQLErrors[0]?.extensions?.response?.body?.messages
    return typeof messages === 'string'
      ? messages
      : graphQLErrors[0].extensions.response?.body?.messages?.join('\n') || graphQLErrors[0].message
  } else if (networkError.result) {
    const errors = networkError.result.errors
    return errors[0].message
  } else if (error.message) {
    return error.message
  }
}

export default client
